/**
 * Step 2 heading plus the organization being added, with a back link to the
 * search the user came from.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import { formatOrganizationLocation } from '../helpers';
import { Organization } from '../types';
import styles from '../AddOrganizationToEvents.module.css';

interface SelectedOrganizationCardProps {
  organization: Organization;
  /** Events the organization is already in; null until the event list has loaded. */
  alreadyInCount: number | null;
  /** Step 1 URL, keeping the user's search when they came from it. */
  backTo: string;
  disabled: boolean;
  onChange: () => void;
}

const SelectedOrganizationCard: React.FC<SelectedOrganizationCardProps> = ({
  organization,
  alreadyInCount,
  backTo,
  disabled,
  onChange
}) => {
  const details = [
    formatOrganizationLocation(organization),
    alreadyInCount
      ? I18n.t(i18nKeys.EVENTS.ALREADY_IN, { count: alreadyInCount })
      : ''
  ]
    .filter(Boolean)
    .join(' · ');

  const backContent = (
    <>
      <Icon name="arrow left" aria-hidden="true" />
      <Translate value={i18nKeys.ORGANIZATION.BACK_TO_SEARCH} />
    </>
  );

  return (
    <>
      {/* A link cannot be disabled, so while submitting it becomes a plain button. */}
      {disabled ? (
        <Button basic size="small" disabled className={styles.backButton}>
          {backContent}
        </Button>
      ) : (
        <Button
          basic
          size="small"
          as={Link}
          to={backTo}
          className={styles.backButton}
        >
          {backContent}
        </Button>
      )}
      <p className={styles.stepLabel}>
        <Translate value={i18nKeys.EVENTS.STEP} />
      </p>
      <h3 className={styles.stepTitle}>
        <Translate value={i18nKeys.EVENTS.TITLE} />
      </h3>
      <p className={styles.muted}>
        <Translate value={i18nKeys.EVENTS.SUBTITLE} />
      </p>

      <Segment className={styles.selectedOrganization}>
        <div>
          <strong>{organization.name}</strong>
          {details && <div className={styles.muted}>{details}</div>}
        </div>
        <Button basic size="small" onClick={onChange} disabled={disabled}>
          <Translate value={i18nKeys.EVENTS.CHANGE_ORGANIZATION} />
        </Button>
      </Segment>
    </>
  );
};

export default SelectedOrganizationCard;
